/**
 * Aggregatr.ts - Data Integration Manager
 * 
 * ARCHITECTURE: Integration Manager Pattern
 * Aggregatr ensures that all data operations have:
 * - Logging (via Loggr) - ALWAYS present
 * - Caching (via Cachr) - ALWAYS present
 * - Profile boundaries (via Profylr) - Optional
 * 
 * This prevents forgetting critical integrations when working with data.
 * 
 * CHANGE HISTORY:
 * - 251125_2110 danielbeer: Refactor to Integration Manager pattern
 * - 251102_1636 danielbeer: Integrate Profylr for data boundaries and filtering
 * - 251027_2101 danielbeer: Move CRUD logic from Actr to Aggregatr
 */
import { MemoryStorage } from './storage/MemoryStorage';
import type { ProfileManager, ResolvedProfile } from '@opcentral/profylr';
import type {
  AggregatrOptions,
  EntityData,
  QueryFilters,
  StorageAdapter,
  DataSource
} from './types/AggregatrTypes';

// TODO: Type when Loggr/Cachr export proper types
type LoggrInstance = any;
type CachrInstance = any;

export class Aggregatr {
  private storage: StorageAdapter;
  private dataSources: Map<string, DataSource>;
  private relationships: Map<string, Map<string, Set<string>>>;
  private debug: boolean;
  private profylr: ProfileManager | null;
  
  // ✅ INTEGRATION MANAGER: Required dependencies
  private loggr: LoggrInstance;
  private cachr: CachrInstance;

  constructor(options: AggregatrOptions = {}) {
    // ✅ ENFORCE: Logging is ALWAYS available
    this.loggr = options.loggr || this._getLoggr();
    
    // ✅ ENFORCE: Caching is ALWAYS available
    this.cachr = options.cachr || this._getCachr();
    
    this.storage = options.storage || new MemoryStorage();
    this.dataSources = new Map();
    this.relationships = new Map();
    this.debug = options.debug || false;
    this.profylr = options.profylr || null;
    
    this._log('info', 'Aggregatr initialized', { debug: this.debug });

    // Add additional data sources
    if (options.dataSources) {
      options.dataSources.forEach(source => {
        this.addDataSource(source.name, source);
      });
    }

    // Initialize storage
    this._initializeStorage();
  }

  /**
   * Get Loggr instance (required dependency)
   */
  private _getLoggr(): LoggrInstance {
    try {
      const { getLoggr } = require('@opcentral/loggr');
      return getLoggr();
    } catch {
      // Fallback to console if Loggr not available
      return {
        log: (level: string, message: string, meta?: any) => {
          if (this.debug) console.log(`[Aggregatr:${level}]`, message, meta);
        }
      };
    }
  }
  
  /**
   * Get Cachr instance (required dependency)
   */
  private _getCachr(): CachrInstance {
    try {
      const { getCachr } = require('@opcentral/cachr');
      return getCachr();
    } catch {
      // Fallback to no-op if Cachr not available
      return {
        get: async () => null,
        set: async () => {},
        has: async () => false,
        delete: async () => false
      };
    }
  }
  
  /**
   * Internal logging helper
   */
  private _log(level: string, message: string, meta?: any): void {
    this.loggr.log(level, message, meta);
  }
  
  /**
   * Initialize storage
   */
  private async _initializeStorage(): Promise<void> {
    if (this.storage.initialize) {
      await this.storage.initialize();
    }
  }

  /**
   * Generate a unique ID
   */
  private _generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get relationship key
   */
  private _getRelationshipKey(entityType: string, id: string): string {
    return `${entityType}:${id}`;
  }

  /**
   * Delete all relationships for an entity
   */
  private _deleteAllRelationships(entityType: string, id: string): void {
    const key = this._getRelationshipKey(entityType, id);
    this.relationships.delete(key);

    // Also remove this entity from other entities' relationships
    for (const [, relationTypes] of this.relationships) {
      for (const [, targetIds] of relationTypes) {
        targetIds.delete(id);
      }
    }
  }

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Create a new entity
   */
  async create(entityType: string, data: EntityData): Promise<EntityData> {
    try {
      // Generate ID if not provided
      const id = data.id || this._generateId();

      this._log('debug', `Creating ${entityType}`, { id, data });

      // Store entity
      const entity = await this.storage.set(entityType, id, { ...data, id });

      return entity;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      if (this.debug) {
        console.error(`[Aggregatr] Create failed for ${entityType}:`, err.message);
      }
      throw err;
    }
  }

  /**
   * Read an entity
   */
  async read(entityType: string, id: string): Promise<EntityData | null> {
    try {
      this._log('debug', `Reading ${entityType}`, { id });

      const entity = await this.storage.get(entityType, id);

      if (!entity) {
        this._log('debug', `${entityType} not found`, { id });
      }

      return entity;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      if (this.debug) {
        console.error(`[Aggregatr] Read failed for ${entityType}:${id}:`, err.message);
      }
      throw err;
    }
  }

  /**
   * Update an entity
   */
  async update(entityType: string, id: string, updates: Partial<EntityData>): Promise<EntityData> {
    try {
      // Get existing entity
      const existing = await this.storage.get(entityType, id);
      if (!existing) {
        throw new Error(`${entityType}:${id} not found`);
      }

      this._log('debug', `Updating ${entityType}`, { id, updates });

      // Merge updates
      const updated: EntityData = {
        ...existing,
        ...updates,
        id, // Preserve ID
        _type: entityType, // Preserve type
        _createdAt: (existing as any)._createdAt, // Preserve creation time
        _updatedAt: Date.now()
      };

      // Store updated entity
      const entity = await this.storage.set(entityType, id, updated);

      return entity;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      if (this.debug) {
        console.error(`[Aggregatr] Update failed for ${entityType}:${id}:`, err.message);
      }
      throw err;
    }
  }

  /**
   * Delete an entity
   */
  async delete(entityType: string, id: string): Promise<boolean> {
    try {
      this._log('debug', `Deleting ${entityType}`, { id });

      // Delete entity
      const deleted = await this.storage.delete(entityType, id);

      if (deleted) {
        // Delete all relationships
        this._deleteAllRelationships(entityType, id);
      } else {
        this._log('debug', `${entityType} not found for deletion`, { id });
      }

      return deleted;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      if (this.debug) {
        console.error(`[Aggregatr] Delete failed for ${entityType}:${id}:`, err.message);
      }
      throw err;
    }
  }

  /**
   * Get active profile from Profylr
   */
  async getActiveProfile(): Promise<ResolvedProfile | null> {
    if (!this.profylr) {
      return null;
    }
    
    try {
      return await this.profylr.getActiveProfile();
    } catch (error) {
      if (this.debug) {
        console.warn('[Aggregatr] Failed to get active profile:', error);
      }
      return null;
    }
  }

  /**
   * Apply profile data boundaries to query filters
   * Enhances filters with profile-based restrictions (datasets, tags, dateRange)
   */
  private async applyDataBoundaries(
    entityType: string,
    filters?: QueryFilters
  ): Promise<QueryFilters> {
    const profile = await this.getActiveProfile();
    
    if (!profile?.dataBoundaries) {
      return filters || {}; // No profile or no data boundaries
    }
    
    const boundaries = profile.dataBoundaries;
    const enhancedFilters: QueryFilters = { ...filters };
    
    // Apply dataset restrictions
    if (boundaries.datasets && boundaries.datasets.length > 0) {
      enhancedFilters._allowedDatasets = boundaries.datasets;
    }
    
    // Apply dataset exclusions
    if (boundaries.excludeDatasets && boundaries.excludeDatasets.length > 0) {
      enhancedFilters._excludedDatasets = boundaries.excludeDatasets;
    }
    
    // Apply tag restrictions
    if (boundaries.tags && boundaries.tags.length > 0) {
      enhancedFilters._requiredTags = boundaries.tags;
    }
    
    // Apply date range restrictions
    if (boundaries.dateRange) {
      if (boundaries.dateRange.start) {
        enhancedFilters._dateStart = boundaries.dateRange.start;
      }
      if (boundaries.dateRange.end) {
        enhancedFilters._dateEnd = boundaries.dateRange.end;
      }
    }
    
    // Apply custom filters from profile
    if (boundaries.filters) {
      Object.assign(enhancedFilters, boundaries.filters);
    }
    
    return enhancedFilters;
  }

  /**
   * Filter entities based on profile data boundaries
   * Post-query filtering for boundaries that can't be applied at storage level
   */
  private filterByDataBoundaries(
    entities: EntityData[],
    profile: ResolvedProfile | null
  ): EntityData[] {
    if (!profile?.dataBoundaries) {
      return entities;
    }
    
    const boundaries = profile.dataBoundaries;
    
    return entities.filter(entity => {
      // Check dataset restrictions
      if (boundaries.datasets && boundaries.datasets.length > 0) {
        const entityDataset = entity._dataset || entity.dataset;
        if (entityDataset && !boundaries.datasets.includes(entityDataset)) {
          return false;
        }
      }
      
      // Check dataset exclusions
      if (boundaries.excludeDatasets && boundaries.excludeDatasets.length > 0) {
        const entityDataset = entity._dataset || entity.dataset;
        if (entityDataset && boundaries.excludeDatasets.includes(entityDataset)) {
          return false;
        }
      }
      
      // Check tag requirements
      if (boundaries.tags && boundaries.tags.length > 0) {
        const entityTags = entity.tags || [];
        const hasAllRequiredTags = boundaries.tags.every(tag => 
          entityTags.includes(tag)
        );
        if (!hasAllRequiredTags) {
          return false;
        }
      }
      
      // Check date range
      if (boundaries.dateRange) {
        const entityDate = entity.createdAt || entity.updatedAt || entity.date;
        if (entityDate) {
          const date = new Date(entityDate);
          if (boundaries.dateRange.start && date < new Date(boundaries.dateRange.start)) {
            return false;
          }
          if (boundaries.dateRange.end && date > new Date(boundaries.dateRange.end)) {
            return false;
          }
        }
      }
      
      return true;
    });
  }

  /**
   * Query entities with profile-based data boundaries
   * 
   * PROFYLR INTEGRATION:
   * 1. Get active profile from Profylr
   * 2. Apply profile data boundaries to query filters
   * 3. Execute query with enhanced filters
   * 4. Post-filter results based on profile boundaries
   */
  async query(entityType: string, filters?: QueryFilters): Promise<EntityData[]> {
    try {
      this._log('debug', `Querying ${entityType}`, { filters });

      // Apply profile data boundaries to filters
      const enhancedFilters = await this.applyDataBoundaries(entityType, filters);

      // Execute query with enhanced filters
      const entities = await this.storage.query(entityType, enhancedFilters);

      // Post-filter based on profile boundaries
      const profile = await this.getActiveProfile();
      const filteredEntities = this.filterByDataBoundaries(entities, profile);

      if (profile?.dataBoundaries) {
        this._log('debug', 'Applied profile data boundaries', {
          before: entities.length,
          after: filteredEntities.length
        });
      }

      return filteredEntities;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      if (this.debug) {
        console.error(`[Aggregatr] Query failed for ${entityType}:`, err.message);
      }
      throw err;
    }
  }

  // ============================================================================
  // RELATIONSHIP MANAGEMENT
  // ============================================================================

  /**
   * Create a relationship between entities
   */
  async relate(
    entityType: string,
    id: string,
    relationType: string,
    targetId: string
  ): Promise<void> {
    const key = this._getRelationshipKey(entityType, id);

    if (!this.relationships.has(key)) {
      this.relationships.set(key, new Map());
    }

    const entityRelations = this.relationships.get(key)!;

    if (!entityRelations.has(relationType)) {
      entityRelations.set(relationType, new Set());
    }

    entityRelations.get(relationType)!.add(targetId);

    this._log('debug', 'Created relationship', {
      entityType,
      id,
      relationType,
      targetId
    });
  }

  /**
   * Remove a relationship between entities
   */
  async unrelate(
    entityType: string,
    id: string,
    relationType: string,
    targetId: string
  ): Promise<void> {
    const key = this._getRelationshipKey(entityType, id);
    const entityRelations = this.relationships.get(key);

    if (entityRelations) {
      const targets = entityRelations.get(relationType);
      if (targets) {
        targets.delete(targetId);

        // Clean up empty sets
        if (targets.size === 0) {
          entityRelations.delete(relationType);
        }
      }

      // Clean up empty maps
      if (entityRelations.size === 0) {
        this.relationships.delete(key);
      }
    }

    this._log('debug', 'Removed relationship', {
      entityType,
      id,
      relationType,
      targetId
    });
  }

  /**
   * Get related entities
   */
  async getRelated(
    entityType: string,
    id: string,
    relationType: string
  ): Promise<EntityData[]> {
    const key = this._getRelationshipKey(entityType, id);
    const entityRelations = this.relationships.get(key);

    if (!entityRelations) {
      return [];
    }

    const targetIds = entityRelations.get(relationType);
    if (!targetIds || targetIds.size === 0) {
      return [];
    }

    // Fetch all related entities
    // Note: We don't know the target entity type, so we search all types
    // This could be optimized with a type hint parameter
    const related: EntityData[] = [];

    for (const targetId of targetIds) {
      // Try to find in same entity type first
      let entity = await this.storage.get(entityType, targetId);

      // If not found, we'd need to search other types
      // For now, just return what we find
      if (entity) {
        related.push(entity);
      }
    }

    return related;
  }

  // ============================================================================
  // DATA SOURCE MANAGEMENT
  // ============================================================================

  /**
   * Add a data source for aggregation
   */
  addDataSource(name: string, source: DataSource): void {
    this.dataSources.set(name, source);

    this._log('info', 'Added data source', { name, type: source.type });
  }

  /**
   * Remove a data source
   */
  removeDataSource(name: string): void {
    this.dataSources.delete(name);

    this._log('info', 'Removed data source', { name });
  }

  /**
   * Get a data source
   */
  getDataSource(name: string): DataSource | null {
    return this.dataSources.get(name) || null;
  }

  /**
   * Get all data sources
   */
  getDataSources(): DataSource[] {
    return Array.from(this.dataSources.values());
  }

  // ============================================================================
  // AGGREGATION (Future Enhancement)
  // ============================================================================

  /**
   * Aggregate data from multiple sources
   * TODO: Implement full aggregation logic
   */
  async aggregate(
    sources: string[],
    entityType: string,
    filters?: QueryFilters
  ): Promise<EntityData[]> {
    const results: EntityData[] = [];

    // Query primary storage
    const primaryResults = await this.query(entityType, filters);
    results.push(...primaryResults);

    // Query additional sources
    for (const sourceName of sources) {
      const source = this.dataSources.get(sourceName);
      if (source) {
        try {
          const sourceResults = await source.adapter.query(entityType, filters);
          results.push(...sourceResults);
        } catch (error) {
          if (this.debug) {
            console.error(`[Aggregatr] Failed to query source ${sourceName}:`, error);
          }
        }
      }
    }

    return results;
  }

  /**
   * Get storage statistics
   */
  getStats(): Record<string, any> {
    const stats: Record<string, any> = {
      dataSources: this.dataSources.size,
      relationships: this.relationships.size
    };

    // Add storage-specific stats if available
    if ('getStats' in this.storage && typeof (this.storage as any).getStats === 'function') {
      stats.storage = (this.storage as any).getStats();
    }

    return stats;
  }

  // ============================================================================
  // FETCH/SAVE OPERATIONS (Universal Data Source Access)
  // ============================================================================

  /**
   * Fetch data from any source
   * @param options - Fetch options including source configuration
   */
  async fetch(options: {
    source: {
      type: string;
      url?: string;
      path?: string;
      [key: string]: any;
    };
    transform?: (data: any) => any;
  }): Promise<any> {
    const { source, transform } = options;

    try {
      let data: any;

      // Route to appropriate fetcher based on source type
      switch (source.type) {
        case 'url':
        case 'api':
          data = await this._fetchFromUrl(source);
          break;
        
        case 'local':
          data = await this._fetchFromFile(source);
          break;
        
        case 'figma':
          data = await this._fetchFromFigma(source);
          break;
        
        case 'github':
          data = await this._fetchFromGithub(source);
          break;
        
        default:
          // Check if we have a registered data source adapter
          const dataSource = this.dataSources.get(source.type);
          if (dataSource && 'fetch' in dataSource.adapter && typeof (dataSource.adapter as any).fetch === 'function') {
            data = await (dataSource.adapter as any).fetch(source);
          } else {
            throw new Error(`Unsupported source type: ${source.type}`);
          }
      }

      // Apply transformation if provided
      if (transform) {
        data = transform(data);
      }

      return data;
    } catch (error) {
      if (this.debug) {
        console.error('[Aggregatr] Fetch failed:', error);
      }
      throw error;
    }
  }

  /**
   * Save data to any destination
   * @param options - Save options including destination configuration
   */
  async save(options: {
    destination: {
      type: string;
      path?: string;
      [key: string]: any;
    };
    data: any;
    format?: 'json' | 'binary' | 'text';
  }): Promise<any> {
    const { destination, data, format = 'json' } = options;

    try {
      // Route to appropriate saver based on destination type
      switch (destination.type) {
        case 'local':
          return await this._saveToFile(destination, data, format);
        
        case 'api':
          return await this._saveToApi(destination, data);
        
        default:
          // Check if we have a registered data source adapter
          const dataSource = this.dataSources.get(destination.type);
          if (dataSource && 'save' in dataSource.adapter && typeof (dataSource.adapter as any).save === 'function') {
            return await (dataSource.adapter as any).save(destination, data);
          } else {
            throw new Error(`Unsupported destination type: ${destination.type}`);
          }
      }
    } catch (error) {
      if (this.debug) {
        console.error('[Aggregatr] Save failed:', error);
      }
      throw error;
    }
  }

  /**
   * Fetch from URL/API
   * Delegates to Grabbr for all URL fetching
   */
  private async _fetchFromUrl(source: any): Promise<any> {
    // Check if Grabbr is available
    let Grabbr;
    try {
      const grabbrModule = await import('@opcentral/grabbr');
      Grabbr = grabbrModule.Grabbr;
    } catch (error) {
      if (this.debug) {
        console.warn('[Aggregatr] Grabbr not available, falling back to fetch');
      }
      // Fallback to native fetch if Grabbr not available
      return this._fetchFromUrlFallback(source);
    }

    const { url, method = 'GET', headers = {}, params, body } = source;
    
    // Build URL with params
    let fullUrl = url;
    if (params) {
      const searchParams = new URLSearchParams(params);
      fullUrl = `${url}?${searchParams.toString()}`;
    }

    // Use Grabbr for fetching
    const options: any = {
      method,
      headers,
      cache: true  // Enable Grabbr's caching
    };

    if (body) {
      options.body = typeof body === 'string' ? body : JSON.stringify(body);
    }

    // Grabbr.fetchWithType returns { data, type }
    const result = await Grabbr.fetchWithType(fullUrl, options);
    
    return result.data;
  }

  /**
   * Fallback fetch implementation when Grabbr is not available
   */
  private async _fetchFromUrlFallback(source: any): Promise<any> {
    const { url, method = 'GET', headers = {}, params, body } = source;
    
    // Build URL with params
    let fullUrl = url;
    if (params) {
      const searchParams = new URLSearchParams(params);
      fullUrl = `${url}?${searchParams.toString()}`;
    }

    const response = await fetch(fullUrl, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Try to parse as JSON, fall back to text
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      return await response.json();
    } else {
      return await response.text();
    }
  }

  /**
   * Fetch from local file
   */
  private async _fetchFromFile(_source: any): Promise<any> {
    // This would need Node.js fs module or browser File API
    // For now, throw error indicating it needs implementation
    throw new Error('Local file fetching requires platform-specific implementation');
  }

  /**
   * Fetch from Figma API
   */
  private async _fetchFromFigma(source: any): Promise<any> {
    const { fileKey, token, nodeIds } = source;
    
    if (!fileKey || !token) {
      throw new Error('Figma source requires fileKey and token');
    }

    // Fetch file data
    const fileUrl = `https://api.figma.com/v1/files/${fileKey}`;
    const fileData = await this._fetchFromUrl({
      url: fileUrl,
      headers: { 'X-Figma-Token': token }
    });

    // If nodeIds specified, also fetch images
    if (nodeIds && nodeIds.length > 0) {
      const imagesUrl = `https://api.figma.com/v1/images/${fileKey}`;
      const imagesData = await this._fetchFromUrl({
        url: imagesUrl,
        params: {
          ids: nodeIds.join(','),
          format: 'png',
          scale: '2'
        },
        headers: { 'X-Figma-Token': token }
      });

      return {
        file: fileData,
        images: imagesData
      };
    }

    return fileData;
  }

  /**
   * Fetch from GitHub API
   */
  private async _fetchFromGithub(source: any): Promise<any> {
    const { owner, repo, path, branch = 'main', token } = source;
    
    if (!owner || !repo) {
      throw new Error('GitHub source requires owner and repo');
    }

    const url = path
      ? `https://api.github.com/repos/${owner}/${repo}/contents/${path}?ref=${branch}`
      : `https://api.github.com/repos/${owner}/${repo}`;

    const headers: any = {
      'Accept': 'application/vnd.github.v3+json'
    };

    if (token) {
      headers['Authorization'] = `token ${token}`;
    }

    return await this._fetchFromUrl({ url, headers });
  }

  /**
   * Save to local file
   */
  private async _saveToFile(_destination: any, _data: any, _format: string): Promise<any> {
    // This would need Node.js fs module or browser File API
    // For now, throw error indicating it needs implementation
    throw new Error('Local file saving requires platform-specific implementation');
  }

  /**
   * Save to API
   */
  private async _saveToApi(destination: any, data: any): Promise<any> {
    const { url, method = 'POST', headers = {} } = destination;
    
    return await this._fetchFromUrl({
      url,
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      body: data
    });
  }
}

export default Aggregatr;
